import { get, set, del } from 'idb-keyval';
import type { EditorProject, SavedProject, ProjectIndexEntry } from '../types';
import { CURRENT_SCHEMA_VERSION, migrateProject } from './projectMigrations';

const PROJECT_PREFIX = 'vidnight-project:';
const INDEX_KEY = 'vidnight-project-index';
const LAST_ACTIVE_LS_KEY = 'vidnight-last-active-project';
const LAST_ACTIVE_IDB_KEY = 'vidnight-last-active-project-id';

function projectKey(id: string) {
  return `${PROJECT_PREFIX}${id}`;
}

/** Save project to IDB + update index + set lastActive */
export async function saveProject(project: EditorProject, thumbnailUrl?: string | null): Promise<void> {
  const saved: SavedProject = {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    project,
    savedAt: Date.now(),
  };

  await set(projectKey(project.id), saved);
  await upsertIndex(project, thumbnailUrl ?? null);
  setLastActiveProjectId(project.id);
}

/** Load project from IDB, running migrations if needed */
export async function loadProject(id: string): Promise<SavedProject | null> {
  const saved = await get<SavedProject>(projectKey(id));
  if (!saved) return null;
  return migrateProject(saved);
}

/** Delete project + remove from index */
export async function deleteProject(id: string): Promise<void> {
  await del(projectKey(id));
  const index = await getProjectIndex();
  await set(INDEX_KEY, index.filter((e) => e.id !== id));
  clearLastActiveProjectId(id);
}

/** Get project index (lightweight list) */
export async function getProjectIndex(): Promise<ProjectIndexEntry[]> {
  return (await get<ProjectIndexEntry[]>(INDEX_KEY)) ?? [];
}

/** Upsert project in index */
async function upsertIndex(project: EditorProject, thumbnailUrl: string | null): Promise<void> {
  const index = await getProjectIndex();
  const existing = index.find((e) => e.id === project.id);
  const entry: ProjectIndexEntry = {
    id: project.id,
    name: project.name,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    thumbnailUrl: thumbnailUrl ?? existing?.thumbnailUrl ?? null,
    durationMs: project.durationMs,
    trackCount: project.tracks.length,
  };

  const existingIdx = index.findIndex((e) => e.id === project.id);
  if (existingIdx >= 0) {
    index[existingIdx] = entry;
  } else {
    index.unshift(entry);
  }

  await set(INDEX_KEY, index);
}

// --- Last active project (localStorage primary, IDB fallback) ---

export function getLastActiveProjectId(): string | null {
  try {
    return localStorage.getItem(LAST_ACTIVE_LS_KEY);
  } catch {
    // localStorage unavailable (e.g. private browsing quota)
    return null;
  }
}

export function setLastActiveProjectId(id: string): void {
  try {
    localStorage.setItem(LAST_ACTIVE_LS_KEY, id);
  } catch {
    // fallback to IDB
    set(LAST_ACTIVE_IDB_KEY, id).catch(() => {});
  }
}

export function clearLastActiveProjectId(onlyIfMatchesId?: string): void {
  try {
    if (onlyIfMatchesId) {
      const current = localStorage.getItem(LAST_ACTIVE_LS_KEY);
      if (current !== onlyIfMatchesId) return;
    }
    localStorage.removeItem(LAST_ACTIVE_LS_KEY);
  } catch {
    del(LAST_ACTIVE_IDB_KEY).catch(() => {});
  }
}

/** Sync write for beforeunload — must not be async */
export function syncSetLastActive(id: string): void {
  try {
    localStorage.setItem(LAST_ACTIVE_LS_KEY, id);
  } catch {
    // best-effort
  }
}
